import React from "react";
import { ChevronRight } from "@material-ui/icons";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl";
import { CardContent, List, ListItem, ListItemIcon, ListItemText, Typography } from "@material-ui/core";

import { CardPageItem } from "../../../../custom/Page";
import useHasPermissions from "../../../../hooks/useHasPermission";
import { DIALOG_OPTION, HierarchyViewTypeEnum, Permissions, Roles } from "../../../../util";
import FormChangeRecommender from "../../FormChangeRecommender";
import { changeRecommender } from "../../../../actions";

import AgreementConfig from "../AgreementConfig";

import ChangeCommissionLimit from "../Dialogs/ChangeCommissionLimit";
import PromoteToAgentDialog from "./PromoteToAgentDialog";
import DemoteFromAgentDialog from "./DemoteFromAgentDialog";
import RestartAgreementDialog from "./RestartAgreementDialog";
import ReadAgreementDialog from "./ReadAgreementDialog";
import ArchiveUserDialog from "./ArchiveUserDialog";
import RemoveAgentDialog from "./RemoveAgentDialog";
import ChangeAgentDialog from "./ChangeAgentDialog";
import RemoveRecommender from "../Dialogs/RemoveRecommender";

const UserManagementActions = React.memo(({ user, currentUser, fetchUser, history }) => {
  const [selectedDialog, setSelectedDialog] = React.useState(null);
  const { hasPermission } = useHasPermissions(user);

  const userIsAgent = React.useMemo(() => [Roles.Agent, Roles.AgentsManager].includes(user.role), [user.role]);

  const handleDialog = React.useCallback((dialog) => setSelectedDialog(dialog), []);
  const handleClose = React.useCallback(() => setSelectedDialog(null), []);

  const agreementItems = React.useMemo(
    () =>
      [
        {
          id: "read-agreement",
          dialogOption: DIALOG_OPTION.READ_AGREEMENT,
          canShow: true,
          label: "app.contractAgent.read",
          onClick: () => handleDialog(DIALOG_OPTION.READ_AGREEMENT),
          DialogComponent: ReadAgreementDialog,
          dialogProps: { onClose: handleClose, user, currentUser },
        },
        {
          id: "restart-agreement",
          dialogOption: DIALOG_OPTION.RESTART_AGREEMENT,
          canShow: hasPermission(Permissions.RESET_AGREEMENT_STATUS),
          label: "app.contractAgent.restart",
          onClick: () => handleDialog(DIALOG_OPTION.RESTART_AGREEMENT),
          DialogComponent: RestartAgreementDialog,
          dialogProps: { onClose: handleClose, user, fetchUser },
        },
      ].filter(({ canShow }) => canShow),
    [hasPermission, handleDialog, handleClose, user, currentUser, fetchUser],
  );

  const actionsMenu = React.useMemo(
    () =>
      [
        {
          id: "change-agent",
          dialogOption: DIALOG_OPTION.CHANGE_AGENT,
          canShow: hasPermission(Permissions.CHANGE_AGENT),
          label: "app.changeAgent",
          onClick: () => handleDialog(DIALOG_OPTION.CHANGE_AGENT),
          DialogComponent: ChangeAgentDialog,
          dialogProps: { onClose: handleClose, user, currentUser, fetchUser },
        },
        {
          id: "clear-agent",
          dialogOption: DIALOG_OPTION.REMOVE_AGENT,
          canShow: hasPermission(Permissions.CLEAR_AGENT),
          label: "app.agentOrAgentManager.clear",
          onClick: () => handleDialog(DIALOG_OPTION.REMOVE_AGENT),
          DialogComponent: RemoveAgentDialog,
          dialogProps: { onClose: handleClose, user, onComplete: history.goBack },
        },
        {
          id: "demote-agent",
          dialogOption: DIALOG_OPTION.DEMOTE_FROM_AGENT,
          canShow: userIsAgent && hasPermission([Permissions.DEMOTE_FROM_AGENT, Permissions.PROMOTE_TO_AGENT]),
          label: "app.agent.lower",
          onClick: () => handleDialog(DIALOG_OPTION.DEMOTE_FROM_AGENT),
          DialogComponent: DemoteFromAgentDialog,
          dialogProps: { onClose: handleClose, user, fetchUser },
        },
        {
          id: "promote-agent",
          dialogOption: DIALOG_OPTION.PROMOTE_TO_AGENT,
          canShow: !userIsAgent && hasPermission([Permissions.DEMOTE_FROM_AGENT, Permissions.PROMOTE_TO_AGENT]),
          label: "app.agent.promote",
          onClick: () => handleDialog(DIALOG_OPTION.PROMOTE_TO_AGENT),
          DialogComponent: PromoteToAgentDialog,
          dialogProps: {
            onClose: handleClose,
            user,
            fetchUser,
            onPromoted: () => handleDialog(DIALOG_OPTION.CHANGE_COMMISSION_LIMIT),
          },
        },
        {
          id: "archive-user",
          dialogOption: DIALOG_OPTION.ARCHIVE,
          canShow: hasPermission(Permissions.ARCHIVE),
          label: "app.user.archive",
          onClick: () => handleDialog(DIALOG_OPTION.ARCHIVE),
          DialogComponent: ArchiveUserDialog,
          dialogProps: { onClose: handleClose, user, onComplete: history.goBack },
        },
        {
          id: "edit-recommender",
          dialogOption: DIALOG_OPTION.EDIT_RECOMMENDER,
          canShow: hasPermission([Permissions.EDIT_RECOMMENDER, Permissions.REMOVE_RECOMMENDER]),
          label: user.recommender ? "app.changeRecommender" : "app.addRecommender",
          onClick: () => handleDialog(DIALOG_OPTION.EDIT_RECOMMENDER),
          DialogComponent: FormChangeRecommender,
          dialogProps: {
            onClose: handleClose,
            onSuccess: fetchUser,
            user,
            currentUser,
            hierarchyViewType: HierarchyViewTypeEnum.ALL,
            actionEditRecommender: changeRecommender,
          },
        },
        {
          id: "remove-recommender",
          dialogOption: DIALOG_OPTION.REMOVE_RECOMMENDER,
          canShow: !!user.recommender && hasPermission([Permissions.EDIT_RECOMMENDER, Permissions.REMOVE_RECOMMENDER]),
          label: "app.recommender.clear",
          onClick: () => handleDialog(DIALOG_OPTION.REMOVE_RECOMMENDER),
          DialogComponent: RemoveRecommender,
          dialogProps: { onClose: handleClose, fetchUser, user },
        },
      ].filter(({ canShow }) => canShow),
    [hasPermission, userIsAgent, handleDialog, handleClose, user, currentUser, fetchUser, history],
  );

  return (
    <>
      {userIsAgent && (
        <>
          <CardPageItem raised>
            <CardContent>
              <Typography
                variant="subtitle1"
                align="center"
              >
                <FormattedHTMLMessage id="app.contractAgent" />
              </Typography>
              <List>
                {agreementItems.map(({ id, label, onClick }) => (
                  <ListItem
                    key={id}
                    button
                    onClick={onClick}
                  >
                    <ListItemText
                      primary={
                        <Typography>
                          <FormattedHTMLMessage id={label} />
                        </Typography>
                      }
                    />
                    <ListItemIcon>
                      <ChevronRight />
                    </ListItemIcon>
                  </ListItem>
                ))}
              </List>

              {agreementItems.map(({ id, dialogOption, DialogComponent, dialogProps }) => (
                <DialogComponent
                  key={dialogOption}
                  open={selectedDialog === dialogOption}
                  {...dialogProps}
                />
              ))}
            </CardContent>
          </CardPageItem>

          <CardPageItem raised>
            <CardContent>
              <AgreementConfig
                currentUser={currentUser}
                user={user}
                fetchUser={fetchUser}
              />
            </CardContent>
          </CardPageItem>
        </>
      )}

      {actionsMenu.length > 0 && (
        <CardPageItem raised>
          <CardContent>
            <Typography
              variant="subtitle1"
              align="center"
            >
              <FormattedMessage id="app.actions" />
            </Typography>
            <List>
              {actionsMenu.map(({ id, label, onClick }) => (
                <ListItem
                  key={id}
                  button
                  onClick={onClick}
                >
                  <ListItemText
                    primary={
                      <Typography>
                        <FormattedMessage id={label} />
                      </Typography>
                    }
                  />
                  <ListItemIcon>
                    <ChevronRight />
                  </ListItemIcon>
                </ListItem>
              ))}
            </List>

            {actionsMenu.map(({ id, dialogOption, DialogComponent, dialogProps }) => (
              <DialogComponent
                key={dialogOption}
                open={selectedDialog === dialogOption}
                {...dialogProps}
              />
            ))}
          </CardContent>
        </CardPageItem>
      )}

      {selectedDialog === DIALOG_OPTION.CHANGE_COMMISSION_LIMIT && (
        <ChangeCommissionLimit
          user={user}
          currentUser={currentUser}
          open
          handleCloseDialog={handleClose}
          fetchUser={fetchUser}
        />
      )}
    </>
  );
});

export default UserManagementActions;
